import { getAuth } from 'firebase/auth';
import { doc, getDoc, setDoc, collection, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';

// واجهة النسخة الاحتياطية في Dropbox
export interface DropboxBackup {
  id: string;
  name: string;
  createdAt: Date;
  createdBy: string;
  size: number;
  dropboxFileId: string;
  downloadUrl?: string;
  collections?: string[];
  isScheduled?: boolean;
  scheduleType?: 'daily' | 'weekly' | 'monthly';
}

// واجهة إعدادات النسخ الاحتياطي في Dropbox
export interface DropboxBackupSettings {
  enabled: boolean;
  autoUpload: boolean;
  autoDelete: boolean;
  maxBackups: number;
  folderName: string;
  accessToken?: string;
}

// الإعدادات الافتراضية
const DEFAULT_SETTINGS: DropboxBackupSettings = {
  enabled: false,
  autoUpload: false,
  autoDelete: false,
  maxBackups: 10,
  folderName: 'MaintenanceSystemBackups'
};

/**
 * الحصول على إعدادات النسخ الاحتياطي في Dropbox
 * @returns إعدادات النسخ الاحتياطي
 */
export const getDropboxBackupSettings = async (): Promise<DropboxBackupSettings> => {
  try {
    // الحصول على الإعدادات من Firestore
    const settingsDoc = await getDoc(doc(db, 'settings', 'dropboxBackup'));
    
    if (settingsDoc.exists()) {
      return settingsDoc.data() as DropboxBackupSettings;
    }
    
    // حفظ الإعدادات الافتراضية
    await setDoc(doc(db, 'settings', 'dropboxBackup'), DEFAULT_SETTINGS);
    
    return DEFAULT_SETTINGS;
  } catch (error) {
    console.error('Error getting dropbox backup settings:', error);
    throw new Error('فشل في الحصول على إعدادات النسخ الاحتياطي في Dropbox: ' + (error instanceof Error ? error.message : 'خطأ غير معروف'));
  }
};

/**
 * حفظ إعدادات النسخ الاحتياطي في Dropbox
 * @param settings إعدادات النسخ الاحتياطي
 */
export const saveDropboxBackupSettings = async (settings: DropboxBackupSettings): Promise<void> => {
  try {
    await setDoc(doc(db, 'settings', 'dropboxBackup'), {
      ...settings,
      updatedAt: new Date()
    });
    
    console.log('Dropbox backup settings saved successfully');
  } catch (error) {
    console.error('Error saving dropbox backup settings:', error);
    throw new Error('فشل في حفظ إعدادات النسخ الاحتياطي في Dropbox: ' + (error instanceof Error ? error.message : 'خطأ غير معروف'));
  }
};

/**
 * إنشاء بيانات النسخة الاحتياطية من قاعدة البيانات
 * @returns بيانات النسخة الاحتياطية
 */
export const createBackupData = async (): Promise<any> => {
  try {
    const backupData: any = {
      timestamp: new Date().toISOString(),
      version: '1.0',
      collections: {}
    };

    // قائمة المجموعات المراد نسخها احتياطياً
    const collections = [
      'customers',
      'maintenanceRequests',
      'products',
      'categories',
      'suppliers',
      'expenses',
      'debts',
      'users',
      'settings'
    ];

    // نسخ كل مجموعة
    for (const collectionName of collections) {
      try {
        const collectionRef = collection(db, collectionName);
        const querySnapshot = await getDocs(collectionRef);

        backupData.collections[collectionName] = [];

        querySnapshot.forEach((doc) => {
          const data = doc.data();

          // تحويل Timestamps إلى strings
          const convertedData = convertTimestampsToStrings(data);

          backupData.collections[collectionName].push({
            id: doc.id,
            data: convertedData
          });
        });

        console.log(`Backed up ${backupData.collections[collectionName].length} documents from ${collectionName}`);
      } catch (error) {
        console.error(`Error backing up collection ${collectionName}:`, error);
        // استمر في النسخ حتى لو فشلت مجموعة واحدة
        backupData.collections[collectionName] = [];
      }
    }

    return backupData;
  } catch (error) {
    console.error('Error creating backup data:', error);
    throw new Error('فشل في إنشاء بيانات النسخة الاحتياطية: ' + (error instanceof Error ? error.message : 'خطأ غير معروف'));
  }
};

/**
 * تحويل Timestamps إلى strings
 * @param obj الكائن المراد تحويله
 * @returns الكائن بعد التحويل
 */
const convertTimestampsToStrings = (obj: any): any => {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (typeof obj === 'object' && 'toDate' in obj && typeof obj.toDate === 'function') {
    // هذا Firestore Timestamp
    return obj.toDate().toISOString();
  }

  if (obj instanceof Date) {
    return obj.toISOString();
  }

  if (Array.isArray(obj)) {
    return obj.map(convertTimestampsToStrings);
  }

  if (typeof obj === 'object') {
    const converted: any = {};
    for (const key in obj) {
      converted[key] = convertTimestampsToStrings(obj[key]);
    }
    return converted;
  }

  return obj;
};

/**
 * التحقق من صحة رمز الوصول لـ Dropbox
 * @param accessToken رمز الوصول
 * @returns true إذا كان الرمز صحيحاً
 */
export const validateDropboxToken = async (accessToken: string): Promise<boolean> => {
  try {
    const response = await fetch('https://api.dropboxapi.com/2/users/get_current_account', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      }
    });

    return response.ok;
  } catch (error) {
    console.error('Error validating Dropbox token:', error);
    return false;
  }
};

/**
 * إنشاء نسخة احتياطية وتحميلها إلى Dropbox
 * @param name اسم النسخة الاحتياطية
 * @param backupData بيانات النسخة الاحتياطية
 * @returns معلومات النسخة الاحتياطية
 */
export const createDropboxBackup = async (name: string, customBackupData?: any): Promise<DropboxBackup> => {
  try {
    console.log(`Creating backup: ${name}`);

    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error('يجب تسجيل الدخول لإنشاء نسخة احتياطية');
    }

    // إنشاء بيانات النسخة الاحتياطية
    const backupData = customBackupData || await createBackupData();

    // إنشاء معلومات النسخة الاحتياطية
    const backupId = `backup_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    const fileName = `${name}_${new Date().toISOString().split('T')[0]}.json`;

    const backupInfo: DropboxBackup = {
      id: backupId,
      name: name,
      createdAt: new Date(),
      createdBy: user.email || user.uid,
      size: JSON.stringify(backupData).length,
      dropboxFileId: fileName
    };

    // الحصول على إعدادات Dropbox
    const settings = await getDropboxBackupSettings();

    // إذا تم تكوين Dropbox، حاول الرفع
    if (settings.accessToken) {
      try {
        // التحقق من صحة رمز الوصول
        const isTokenValid = await validateDropboxToken(settings.accessToken);
        if (!isTokenValid) {
          console.warn('رمز الوصول لـ Dropbox غير صحيح، سيتم حفظ النسخة محلياً فقط');
        } else {
          // رفع إلى Dropbox
          const backupJSON = JSON.stringify(backupData, null, 2);
          const dropboxPath = `/${settings.folderName}/${fileName}`;

          const response = await fetch('https://content.dropboxapi.com/2/files/upload', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${settings.accessToken}`,
              'Content-Type': 'application/octet-stream',
              'Dropbox-API-Arg': JSON.stringify({
                path: dropboxPath,
                mode: 'add',
                autorename: true,
                mute: false
              })
            },
            body: backupJSON
          });

          if (response.ok) {
            console.log('تم رفع النسخة الاحتياطية إلى Dropbox بنجاح');
          } else {
            console.warn('فشل في رفع النسخة إلى Dropbox');
          }
        }
      } catch (error) {
        console.warn('فشل في رفع النسخة إلى Dropbox، سيتم حفظها محلياً فقط:', error);
      }
    } else {
      console.log('لم يتم تكوين Dropbox، سيتم حفظ النسخة محلياً فقط');
    }

    // حفظ معلومات النسخة الاحتياطية في localStorage (حل مؤقت)
    const existingBackups = localStorage.getItem(`dropboxBackups_${user.uid}`);
    const backups = existingBackups ? JSON.parse(existingBackups) : [];
    backups.push(backupInfo);
    localStorage.setItem(`dropboxBackups_${user.uid}`, JSON.stringify(backups));

    console.log(`Backup created successfully: ${backupInfo.id}`);
    return backupInfo;
  } catch (error) {
    console.error('Error creating dropbox backup:', error);
    throw new Error('فشل في إنشاء نسخة احتياطية في Dropbox: ' + (error instanceof Error ? error.message : 'خطأ غير معروف'));
  }
};

/**
 * تحميل ملف من Dropbox
 * @param fileId معرف الملف في Dropbox
 * @param accessToken رمز الوصول
 * @returns محتوى الملف
 */
export const downloadFromDropbox = async (fileId: string, accessToken: string): Promise<any> => {
  try {
    const response = await fetch('https://content.dropboxapi.com/2/files/download', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Dropbox-API-Arg': JSON.stringify({
          path: fileId
        })
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`فشل في تحميل الملف من Dropbox: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const fileContent = await response.text();
    return JSON.parse(fileContent);
  } catch (error) {
    console.error('Error downloading from Dropbox:', error);
    throw new Error('فشل في تحميل الملف من Dropbox: ' + (error instanceof Error ? error.message : 'خطأ غير معروف'));
  }
};

/**
 * إنشاء رابط تحميل مباشر للملف (بدون استخدام API)
 * @param fileName اسم الملف
 * @param folderName اسم المجلد
 * @returns رابط تحميل مباشر
 */
export const createDirectDownloadLink = (fileName: string, folderName: string): string => {
  // إنشاء رابط تحميل مباشر بناءً على اسم الملف والمجلد
  // هذا رابط افتراضي يمكن للمستخدم استخدامه إذا كان لديه رابط مشاركة فعلي
  return `تحميل مباشر: ${folderName}/${fileName}`;
};

/**
 * إنشاء رابط تحميل مؤقت للملف
 * @param filePath مسار الملف في Dropbox
 * @param accessToken رمز الوصول
 * @returns رابط تحميل مؤقت
 */
export const createTemporaryDownloadLink = async (filePath: string, accessToken: string): Promise<string> => {
  try {
    const response = await fetch('https://api.dropboxapi.com/2/files/get_temporary_link', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        path: filePath
      })
    });

    if (response.ok) {
      const result = await response.json();
      return result.link;
    }

    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  } catch (error) {
    console.error('Error creating temporary link:', error);
    throw error;
  }
};

/**
 * إنشاء رابط مشاركة لملف في Dropbox
 * @param filePath مسار الملف في Dropbox
 * @param accessToken رمز الوصول
 * @returns رابط المشاركة
 */
export const createDropboxShareLink = async (filePath: string, accessToken: string): Promise<string> => {
  try {
    // أولاً، جرب الحصول على رابط تحميل مؤقت (يعمل حتى مع رموز الوصول المحدودة)
    try {
      const tempLink = await createTemporaryDownloadLink(filePath, accessToken);
      return tempLink;
    } catch (tempError) {
      console.warn('Temporary link failed, trying shared links:', tempError);
    }

    // ثانياً، جرب الحصول على روابط المشاركة الموجودة
    const listResponse = await fetch('https://api.dropboxapi.com/2/sharing/list_shared_links', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        path: filePath,
        direct_only: true
      })
    });

    if (listResponse.ok) {
      const listResult = await listResponse.json();
      if (listResult.links && listResult.links.length > 0) {
        // تحويل رابط المشاركة إلى رابط تحميل مباشر
        const shareUrl = listResult.links[0].url;
        const directUrl = shareUrl.replace('dropbox.com', 'dl.dropboxusercontent.com').replace('?dl=0', '');
        return directUrl;
      }
    }

    // ثالثاً، جرب إنشاء رابط مشاركة جديد
    const response = await fetch('https://api.dropboxapi.com/2/sharing/create_shared_link_with_settings', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        path: filePath,
        settings: {
          requested_visibility: 'public',
          audience: 'public',
          access: 'viewer'
        }
      })
    });

    if (response.ok) {
      const result = await response.json();
      // تحويل رابط المشاركة إلى رابط تحميل مباشر
      const directUrl = result.url.replace('dropbox.com', 'dl.dropboxusercontent.com').replace('?dl=0', '');
      return directUrl;
    }

    // إذا فشل كل شيء، أنشئ رابط إرشادي
    const errorText = await response.text();
    console.warn('All methods failed:', errorText);

    const pathParts = filePath.split('/');
    const fileName = pathParts[pathParts.length - 1];
    const folderName = pathParts[pathParts.length - 2] || 'backup';

    return `https://dropbox.com/home/${folderName}/${fileName}\n\nملاحظة: يرجى إنشاء رابط المشاركة يدوياً من تطبيق Dropbox وتحويله إلى رابط تحميل مباشر بإضافة ?dl=1 في النهاية.`;

  } catch (error) {
    console.error('Error creating share link:', error);

    // في حالة الخطأ، أنشئ رابط إرشادي مع تعليمات
    const pathParts = filePath.split('/');
    const fileName = pathParts[pathParts.length - 1];
    const folderName = pathParts[pathParts.length - 2] || 'backup';

    return `https://dropbox.com/home/${folderName}/${fileName}\n\nتعليمات:\n1. انتقل إلى الرابط أعلاه\n2. انقر بزر الماوس الأيمن على الملف\n3. اختر "مشاركة"\n4. انسخ الرابط وأضف ?dl=1 في النهاية للتحميل المباشر`;
  }
};

/**
 * تحميل قاعدة البيانات من رابط Dropbox
 * @param shareLink رابط المشاركة
 * @returns بيانات قاعدة البيانات
 */
export const downloadDatabaseFromLink = async (shareLink: string): Promise<any> => {
  try {
    // تحويل رابط المشاركة إلى رابط تحميل مباشر
    const directLink = shareLink.replace('dropbox.com', 'dl.dropboxusercontent.com').replace('?dl=0', '');

    const response = await fetch(directLink);

    if (!response.ok) {
      throw new Error(`فشل في تحميل الملف: ${response.status} ${response.statusText}`);
    }

    const fileContent = await response.text();
    return JSON.parse(fileContent);
  } catch (error) {
    console.error('Error downloading database from link:', error);
    throw new Error('فشل في تحميل قاعدة البيانات من الرابط: ' + (error instanceof Error ? error.message : 'خطأ غير معروف'));
  }
};

/**
 * استعادة نسخة احتياطية من Dropbox
 * @param backupId معرف النسخة الاحتياطية
 * @returns بيانات النسخة الاحتياطية
 */
export const restoreDropboxBackup = async (backupId: string): Promise<any> => {
  try {
    console.log(`Restoring dropbox backup: ${backupId}`);
    
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) {
      throw new Error('يجب تسجيل الدخول لاستعادة نسخة احتياطية');
    }
    
    // الحصول على معلومات النسخة الاحتياطية من localStorage
    const existingBackups = localStorage.getItem(`dropboxBackups_${user.uid}`);
    if (!existingBackups) {
      throw new Error(`لا توجد نسخ احتياطية محفوظة`);
    }

    const backups = JSON.parse(existingBackups) as DropboxBackup[];
    const backup = backups.find(b => b.id === backupId);

    if (!backup) {
      throw new Error(`النسخة الاحتياطية غير موجودة: ${backupId}`);
    }
    
    // الحصول على إعدادات Dropbox
    const settings = await getDropboxBackupSettings();
    
    if (!settings.accessToken) {
      throw new Error('لم يتم تكوين Dropbox. يرجى إضافة رمز الوصول في الإعدادات');
    }
    
    // تنزيل الملف من Dropbox
    const response = await fetch('https://content.dropboxapi.com/2/files/download', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${settings.accessToken}`,
        'Dropbox-API-Arg': JSON.stringify({
          path: `/${settings.folderName}/${backup.name.replace(/\s+/g, '_')}_${backup.createdAt.toISOString().split('T')[0]}.json`
        })
      }
    });
    
    if (!response.ok) {
      throw new Error(`فشل في تنزيل الملف من Dropbox: ${response.statusText}`);
    }
    
    const backupJSON = await response.text();
    
    // تحليل البيانات
    const backupData = JSON.parse(backupJSON);
    
    console.log(`Dropbox backup restored successfully: ${backupId}`);
    return backupData;
  } catch (error) {
    console.error('Error restoring dropbox backup:', error);
    throw new Error('فشل في استعادة النسخة الاحتياطية من Dropbox: ' + (error instanceof Error ? error.message : 'خطأ غير معروف'));
  }
};

/**
 * الحصول على قائمة النسخ الاحتياطية في Dropbox
 * @returns قائمة النسخ الاحتياطية
 */
export const getDropboxBackups = async (): Promise<DropboxBackup[]> => {
  try {
    console.log('Getting dropbox backups list');

    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error('يجب تسجيل الدخول لعرض النسخ الاحتياطية');
    }

    // حل مؤقت: استخدام localStorage لحفظ النسخ الاحتياطية
    const localBackups = localStorage.getItem(`dropboxBackups_${user.uid}`);
    if (localBackups) {
      const backups = JSON.parse(localBackups) as DropboxBackup[];
      // تحويل التواريخ من strings إلى Date objects
      backups.forEach(backup => {
        if (typeof backup.createdAt === 'string') {
          backup.createdAt = new Date(backup.createdAt);
        }
      });
      console.log(`Found ${backups.length} dropbox backups in localStorage`);
      return backups.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
    }

    console.log('No dropbox backups found in localStorage');
    return [];
  } catch (error) {
    console.error('Error getting dropbox backups:', error);
    throw new Error('فشل في الحصول على قائمة النسخ الاحتياطية من Dropbox: ' + (error instanceof Error ? error.message : 'خطأ غير معروف'));
  }
};

/**
 * حذف نسخة احتياطية من Dropbox
 * @param backupId معرف النسخة الاحتياطية
 */
export const deleteDropboxBackup = async (backupId: string): Promise<void> => {
  try {
    console.log(`Deleting dropbox backup: ${backupId}`);
    
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) {
      throw new Error('يجب تسجيل الدخول لحذف نسخة احتياطية');
    }
    
    // الحصول على معلومات النسخة الاحتياطية من localStorage
    const existingBackups = localStorage.getItem(`dropboxBackups_${user.uid}`);
    if (!existingBackups) {
      throw new Error(`لا توجد نسخ احتياطية محفوظة`);
    }

    const backups = JSON.parse(existingBackups) as DropboxBackup[];
    const backup = backups.find(b => b.id === backupId);

    if (!backup) {
      throw new Error(`النسخة الاحتياطية غير موجودة: ${backupId}`);
    }
    
    // الحصول على إعدادات Dropbox
    const settings = await getDropboxBackupSettings();
    
    if (!settings.accessToken) {
      throw new Error('لم يتم تكوين Dropbox. يرجى إضافة رمز الوصول في الإعدادات');
    }
    
    // حذف الملف من Dropbox
    const response = await fetch('https://api.dropboxapi.com/2/files/delete_v2', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${settings.accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        path: `/${settings.folderName}/${backup.name.replace(/\s+/g, '_')}_${backup.createdAt.toISOString().split('T')[0]}.json`
      })
    });
    
    if (!response.ok) {
      throw new Error(`فشل في حذف الملف من Dropbox: ${response.statusText}`);
    }
    
    // حذف معلومات النسخة الاحتياطية من localStorage
    const updatedBackups = backups.filter(b => b.id !== backupId);
    localStorage.setItem(`dropboxBackups_${user.uid}`, JSON.stringify(updatedBackups));
    
    console.log(`Dropbox backup deleted successfully: ${backupId}`);
  } catch (error) {
    console.error('Error deleting dropbox backup:', error);
    throw new Error('فشل في حذف النسخة الاحتياطية من Dropbox: ' + (error instanceof Error ? error.message : 'خطأ غير معروف'));
  }
};



// تصدير الخدمة
const dropboxService = {
  getDropboxBackupSettings,
  saveDropboxBackupSettings,
  createDropboxBackup,
  restoreDropboxBackup,
  getDropboxBackups,
  deleteDropboxBackup
};

export default dropboxService; 